<template>
  <div
    v-if="isVisible"
    :class="$style.dropdown"
    @mouseleave="handleMouseLeave"
  >
    <template v-if="hasResults">
      <div v-if="tutorialResults.length > 0" :class="$style.dropdown__section">
        <div :class="$style.dropdown__section_header">
          {{ locales('lc_search_section_tutorials') }}
        </div>
        <div
          v-for="(result, index) in tutorialResults"
          :key="`tutorial-${result.id}`"
          :ref="el => setItemRef(el, `tutorial-${result.id}`)"
          :class="[$style.dropdown__item, { [$style['dropdown__item--hover']]: hoveredItem === `tutorial-${result.id}` || selectedIndex === getTutorialIndex(index) }]"
          @mouseenter="handleMouseEnter(`tutorial-${result.id}`)"
          @click="handleTutorialClick(result)"
        >
          <span :class="$style.dropdown__item_text" v-html="highlightText(result.title, result)" />
        </div>
      </div>

      <div v-if="helpCenterResults.length > 0" :class="$style.dropdown__section">
        <div :class="$style.dropdown__section_header">
          {{ locales('lc_search_section_help_center') }}
        </div>
        <div
          v-for="(result, index) in helpCenterResults"
          :key="`help-${result.id}`"
          :ref="el => setItemRef(el, `help-${result.id}`)"
          :class="[
            $style.dropdown__item,
            {
              [$style['dropdown__item--hover']]: hoveredItem === `help-${result.id}` || selectedIndex === getHelpCenterIndex(index),
              [$style['dropdown__item--see-all']]: result.id === 999
            }
          ]"
          @mouseenter="handleMouseEnter(`help-${result.id}`)"
          @click="handleHelpCenterClick(result)"
        >
          <span :class="$style.dropdown__item_text" v-html="highlightText(result.title, result)" />
          <svg-sprite
            v-if="result.id !== 999 && (hoveredItem === `help-${result.id}` || selectedIndex === getHelpCenterIndex(index))"
            :class="$style.dropdown__item_icon"
            name="link-2"
            type="regular"
            color="#B2B2B2"
            :size="24"
          />
        </div>
      </div>
    </template>

    <div v-else :class="$style.dropdown__empty">
      {{ locales('lc_search_no_results')}}
    </div>
  </div>
</template>

<script>
import { api } from '$$store/api';
import SvgSprite from '$$vueCmp/VueIcon/svgSprite.vue';

const ZENDESK_LOCALE_MAP = {
  'en': 'en-us',
  'en-us': 'en-us',
  'ru': 'ru',
  'ru-ru': 'ru',
  'es': 'es',
  'es-es': 'es',
  'es-mx': 'es',
};

function getHelpCenterLocale(raw) {
  if (!raw) {
    return 'en-us';
  }

  const locale = raw.toLowerCase();

  // Direct match
  if (ZENDESK_LOCALE_MAP[locale]) {
    return ZENDESK_LOCALE_MAP[locale];
  }

  const base = locale.split('-')[0];
  if (ZENDESK_LOCALE_MAP[base]) {
    return ZENDESK_LOCALE_MAP[base];
  }

  return 'en-us';
}

const LOCALE = getHelpCenterLocale(window.user?.locale);
const HELP_CENTER_URL = `https://help.ganttpro.com/hc/${LOCALE}`;
const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'LearningCenterSearchDropdown',
  components: {
    SvgSprite,
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    selectedIndex: {
      type: Number,
      default: -1,
    },
  },
  emits: ['tutorial-click', 'help-center-click', 'close', 'change-selected-index', 'loading'],
  data() {
    return {
      locales: __,
      hoveredItem: null,
      itemRefs: {},
      helpCenterResults: [],
      isLoading: false,
      searchDebounceTimer: null,
      lastSearchQuery: '',
    };
  },
  watch: {
    isLoading(val) {
      this.$emit('loading', val);
    },
    isVisible(newValue) {
      if (newValue) {
        this.hoveredItem = null;
        this.scrollToSelected();
      }
    },
    selectedIndex() {
      this.scrollToSelected();
    },
    searchQuery(newQuery) {
      this.hoveredItem = null;
      this.performSearch(newQuery);
    },
  },
  mounted() {
    if (this.searchQuery && this.searchQuery.trim()) {
      this.performSearch(this.searchQuery);
    }
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  computed: {
    hasResults() {
      return this.helpCenterResults.length > 0;
    },
    allResults() {
      const results = [];
      this.helpCenterResults.forEach((result, index) => {
        results.push({
          type: 'help-center',
          result,
          index: this.getHelpCenterIndex(index),
        });
      });
      return results;
    },
    //TODO: Search by video tutorials is temporarily disabled
    tutorialResults() {
      return [];
    },
  },
  methods: {
    performSearch(query) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      if (!query || !query.trim()) {
        this.helpCenterResults = [];
        this.lastSearchQuery = '';
        this.isLoading = false;
        return;
      }

      const trimmedQuery = query.trim();

      if (trimmedQuery === this.lastSearchQuery) {
        return;
      }

      this.isLoading = true;
      this.searchDebounceTimer = setTimeout(async () => {
        await this.searchArticles(trimmedQuery);
      }, SEARCH_DEBOUNCE_MS);
    },

    async searchArticles(query) {
      if (!query || !query.trim()) {
        this.helpCenterResults = [];
        return;
      }

      this.isLoading = true;
      this.lastSearchQuery = query;

      try {
        const response = await api.get('/help/search', {
          params: {
            query: query.trim(),
            locale: LOCALE,
            limit: 5,
          },
        });

        if (response.data && response.data.articles) {
          const articles = response.data.articles;

          const results = [...articles];
          if (articles.length > 0) {
            results.push({
              id: 999,
              title: this.locales('lc_search_see_all_results'),
              url: `${HELP_CENTER_URL}/search?query=${encodeURIComponent(query)}`,
            });
          }

          this.helpCenterResults = results;
        } else {
          this.helpCenterResults = [];
        }
      } catch (error) {
        console.error('[LearningCenterSearch] Search error:', error);
        this.helpCenterResults = [];
      } finally {
        this.isLoading = false;
      }
    },

    getTutorialIndex(index) {
      return index;
    },
    getHelpCenterIndex(index) {
      // Since tutorialResults is always empty, the index matches the local index
      return index;
    },
    setItemRef(el, key) {
      if (el) {
        this.itemRefs[key] = el;
      }
    },
    handleMouseEnter(itemKey) {
      this.hoveredItem = itemKey;
      const item = this.allResults.find(r => {
        const rKey = r.type === 'tutorial' ? `tutorial-${r.result.id}` : `help-${r.result.id}`;
        return rKey === itemKey;
      });
      if (item) {
        this.$emit('change-selected-index', item.index);
      }
    },
    handleMouseLeave() {
      this.hoveredItem = null;
      this.$emit('change-selected-index', -1);
    },
    scrollToSelected() {
      if (this.selectedIndex < 0) {
        return;
      }
      this.$nextTick(() => {
        const selectedItem = this.allResults[this.selectedIndex];
        if (!selectedItem) {
          return;
        }

        const itemKey = selectedItem.type === 'tutorial'
          ? `tutorial-${selectedItem.result.id}`
          : `help-${selectedItem.result.id}`;
        const element = this.itemRefs[itemKey];

        if (element && this.$el) {
          const dropdownRect = this.$el.getBoundingClientRect();
          const itemRect = element.getBoundingClientRect();

          if (itemRect.bottom > dropdownRect.bottom) {
            element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } else if (itemRect.top < dropdownRect.top) {
            element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }
      });
    },
    selectCurrentItem() {
      if (this.selectedIndex < 0) {
        return;
      }
      const selectedItem = this.allResults[this.selectedIndex];
      if (!selectedItem) {
        return;
      }

      if (selectedItem.type === 'tutorial') {
        this.handleTutorialClick(selectedItem.result);
      } else {
        this.handleHelpCenterClick(selectedItem.result);
      }
    },
    handleTutorialClick(result) {
      this.$emit('tutorial-click', result);
    },
    handleHelpCenterClick(result) {
      this.$emit('help-center-click', result);
    },
    highlightText(text, result = {}) {
      if (!this.searchQuery || !this.searchQuery.trim() || result.id === 999) {
        return text;
      }
      const query = this.searchQuery.trim();
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return text.replace(regex, '<b>$1</b>');
    },
  },
};
</script>

<style module src="./search.less" lang="less"></style>

